// Generated by DesignToCode

import {
  Component,
  forwardRef,
  input,
  signal,
  effect,
  OnInit,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  ControlValueAccessor,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';

@Component({
  selector: 'app-radio-button',
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule, ReactiveFormsModule],
  templateUrl: './radio-button.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButton),
      multi: true,
    },
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class RadioButton implements ControlValueAccessor, OnInit {
  public controlName = input.required<string>();
  public value = input.required<any>();
  public label = input.required<string>();
  public disabled = input(false);

  public selected = signal(false);
  public isDisabled = signal(false);

  // Method to get radio container classes based on state
  public parentContainerClasses = computed(() => {
    const classes: string[] = ['flex', 'items-center', 'group', 'w-fit'];

    if (this.isDisabled()) {
      classes.push('cursor-not-allowed');
    } else {
      classes.push('cursor-pointer');
    }

    return classes.join(' ');
  });

  public radioContainerClasses = computed(() => {
    const classes: string[] = [
      'flex',
      'items-center',
      'justify-center',
      'rounded-max',
      'border-[2px]',
      'w-xl',
      'h-xl',
    ];
    if (this.isDisabled()) {
      classes.push('border-grey-70', 'cursor-not-allowed');
    } else if (this.selected()) {
      classes.push('border-navy-blue-90');
    } else {
      classes.push(
        'border-grey-90',
        'group-hover:border-navy-blue-90',
        'cursor-pointer',
      );
    }

    return classes.join(' ');
  });

  // Method to get inner circle classes based on state
  public innerCircleClasses = computed(() => {
    const classes: string[] = [
      'w-sm',
      'h-sm',
      'rounded-max',
      'transition-all',
      'duration-200',
    ];

    if (this.isDisabled()) {
      classes.push('bg-grey-70');
    } else {
      classes.push('bg-navy-blue-90');
    }

    return classes.join(' ');
  });

  // Method to get label classes based on state
  public labelClasses = computed(() => {
    const classes: string[] = [
      'font-body',
      'font-medium',
      'text-sm',
      'leading-lg ml-xs',
    ];

    if (this.isDisabled()) {
      classes.push('text-grey-90', 'cursor-not-allowed');
    } else if (this.selected()) {
      classes.push('text-grey-120', 'cursor-pointer');
    } else {
      classes.push(
        'text-grey-90',
        'cursor-pointer',
        'group-hover:text-grey-120',
      );
    }

    return classes.join(' ');
  });

  public onChange: any = () => {};
  public onTouched: any = () => {};

  constructor(private formGroupDirective: FormGroupDirective) {
    effect(() => {
      // When inputs change, validate required inputs
      if (this.controlName() === undefined) {
        console.error('ControlName is required for RadioButtonComponent');
      }
      if (this.value() === undefined || this.value() === null) {
        console.error('Value is required for RadioButtonComponent');
      }

      // Sync disabled input signal with internal isDisabled signal
      this.isDisabled.set(this.disabled());
    });
  }

  public ngOnInit(): void {
    // Subscribe to form value changes to keep the state in sync
    const control = this.formGroupDirective.form.get(this.controlName());
    if (control) {
      control.valueChanges.subscribe((value) => {
        this.selected.set(value === this.value());
      });

      // Initialize with the current value
      this.selected.set(control.value === this.value());
    }
  }

  // Handle click events on the custom radio component
  public toggleSelection(): void {
    if (this.isDisabled()) return;

    const control = this.formGroupDirective.form.get(this.controlName());
    if (control) {
      control.setValue(this.value());
      control.markAsTouched();
      this.onTouched();
    }
  }

  // ControlValueAccessor implementation
  public writeValue(value: any): void {
    this.selected.set(value === this.value());
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
